// BindingCodeIssueModal — 共用「產生邀請碼」 modal（Drawer 殼 + IssueForm
// + IssuedCard）。callers：Users 頁的「邀請使用者」 按鈕、BindingCodes 頁
// 的「產生邀請碼」 按鈕。
//
// `onIssued()` fires once per successful issue (not on close) so callers
// can refresh their own list / chip.

import React, { useEffect, useRef, useState } from "react";
import { KeyRound, Copy, CheckCircle2, ExternalLink, AlertTriangle } from "lucide-react";
import QRCode from "qrcode";
import { apiFetch } from "../../lib/api-client.js";
import Drawer from "./Drawer.js";
import { Field } from "./Field.js";
import { ROLE_META, ROLE_GROUPS, roleLabel } from "../lib/roleDisplay.js";

interface IssuedCode {
  code: string;
  redeem_url: string;
  role: string;
  expires_at: number;
}

export type { IssuedCode };

const TTL_OPTIONS = [
  { label: "1 小時", value: 60 * 60 },
  { label: "24 小時", value: 24 * 60 * 60 },
  { label: "7 天 (預設)", value: 7 * 24 * 60 * 60 },
  { label: "30 天", value: 30 * 24 * 60 * 60 },
];

function fmt(ts: number | null): string {
  if (!ts) return "—";
  try {
    return new Date(ts * 1000).toISOString().replace("T", " ").slice(0, 16) + "Z";
  } catch { return String(ts); }
}

async function copyText(t: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(t); return true; }
  catch { return false; }
}

export default function BindingCodeIssueModal({
  open,
  onClose,
  onIssued,
  defaultRole = "manager",
}: {
  open: boolean;
  onClose: () => void;
  onIssued?: () => void;
  defaultRole?: string;
}): React.ReactElement {
  const [issued, setIssued] = useState<IssuedCode | null>(null);

  // Reset state every time modal closes — next open starts clean.
  useEffect(() => {
    if (!open) setIssued(null);
  }, [open]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="產生邀請碼"
      description="選角色 + 到期時間。產生後請立刻複製 → 給目標使用者。"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">關閉</button>
        </div>
      }
    >
      {issued ? (
        <IssuedCard
          issued={issued}
          onAnother={() => setIssued(null)}
        />
      ) : (
        <BindingCodeIssueForm
          defaultRole={defaultRole}
          onIssued={(d) => { setIssued(d); onIssued?.(); }}
        />
      )}
    </Drawer>
  );
}

function IssuedCard(props: { issued: IssuedCode; onAnother: () => void }): React.ReactElement {
  return <BindingCodeIssuedCard issued={props.issued} onAnother={props.onAnother} />;
}

/** Standalone「產生邀請碼」 form — POST /v1/invites, calls onIssued with
 *  the result on success. Exported so AI mode's drawer-form host can
 *  embed this in its right-side drawer instead of jumping to the modal.
 *  Optional `onCancel` adds a sibling「取消」 button next to「產生邀請碼」
 *  — pass it from drawer contexts that need an explicit dismiss button;
 *  modal contexts (which already have a close X) omit it. */
export function BindingCodeIssueForm({
  defaultRole,
  onIssued,
  onCancel,
}: {
  defaultRole: string;
  onIssued: (d: IssuedCode) => void;
  onCancel?: () => void;
}): React.ReactElement {
  const [role, setRole] = useState(defaultRole);
  const [ttl, setTtl] = useState(7 * 24 * 60 * 60);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch("/v1/invites", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ role, ttl_seconds: ttl, note: note.trim() || undefined }),
        credentials: "include",
      });
      const body = await r.json().catch(() => ({})) as {
        ok?: boolean; code?: string; redeem_url?: string; expires_at?: number; role?: string; error?: string;
      };
      if (!r.ok || !body.ok) throw new Error(body.error ?? `HTTP ${r.status}`);
      onIssued({ code: body.code!, redeem_url: body.redeem_url!, role: body.role!, expires_at: body.expires_at! });
    } catch (e) {
      setErr(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="身分" required>
        <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLE_GROUPS.map((g) => (
            <optgroup key={g.kind} label={g.title}>
              {g.roles.map((r) => (
                <option key={r} value={r}>{ROLE_META[r]?.label ?? r}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <RolePickerExplainer role={role} />
      </Field>
      <Field label="到期時間">
        <select className="select" value={ttl} onChange={(e) => setTtl(Number(e.target.value))}>
          {TTL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="備註(選填)" hint="僅管理員看得到，協助辨識用途">
        <input className="input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {err && <div className="rounded-md bg-danger/10 border border-danger/30 p-2 text-2xs text-danger">{err}</div>}
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary text-sm">取消</button>
        )}
        <button type="submit" disabled={busy} className="btn-primary text-sm">
          <KeyRound className="h-4 w-4" /> {busy ? "產生中…" : "產生邀請碼"}
        </button>
      </div>
    </form>
  );
}

/** Inline description + scope warning shown directly under the role
 *  picker. Tenant-wide roles get a yellow warning callout reminding the
 *  operator that「assigning to a single site」 has no effect for these
 *  roles; site-scoped roles get a neutral hint. */
function RolePickerExplainer({ role }: { role: string }): React.ReactElement | null {
  const meta = ROLE_META[role];
  if (!meta) return null;
  if (meta.scope === "tenant") {
    return (
      <div className="mt-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-2xs text-warning leading-relaxed flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span><strong>跨整個單位的身分</strong> — {meta.description}</span>
      </div>
    );
  }
  return (
    <div className="mt-2 text-2xs text-text-muted leading-relaxed">
      {meta.description}
    </div>
  );
}

/** Renders the「已產生」 card — QR + 8 字碼 + 兌換 URL + copy buttons.
 *  Exported so AI chat can drop this inline as a local-only assistant
 *  bubble after the operator confirms the drawer-form (Q1 §6). */
export function BindingCodeIssuedCard({
  issued,
  onAnother,
}: {
  issued: IssuedCode;
  /** Optional — Modal context shows「再產生一個」 button; chat-bubble
   *  context omits it because the operator can ask AI again instead. */
  onAnother?: () => void;
}): React.ReactElement {
  const [copied, setCopied] = useState<"code" | "url" | null>(null);
  // Memoize QR generation by redeem_url so reissuing a code produces a
  // fresh image but unrelated re-renders don't burn CPU.
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const qrLockRef = useRef<string | null>(null);
  useEffect(() => {
    if (qrLockRef.current === issued.redeem_url) return;
    qrLockRef.current = issued.redeem_url;
    QRCode.toDataURL(issued.redeem_url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 256,
      color: { dark: "#0a0a0c", light: "#ffffff" },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [issued.redeem_url]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-success/40 bg-success/5 p-4">
        <div className="flex items-center gap-2 text-success">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm font-semibold">已產生</span>
        </div>
        <div className="mt-3">
          <div className="text-2xs text-text-muted uppercase tracking-wider">8 字邀請碼</div>
          <div className="mt-1 flex items-center gap-2">
            <code className="font-mono text-2xl tracking-[0.4em]">{issued.code}</code>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={async () => { if (await copyText(issued.code)) { setCopied("code"); setTimeout(() => setCopied(null), 1500); } }}
            >
              <Copy className="h-3.5 w-3.5" /> 複製
              {copied === "code" && <span className="text-success">已複製</span>}
            </button>
          </div>
        </div>
        <div className="mt-3">
          <div className="text-2xs text-text-muted uppercase tracking-wider">兌換 URL (含 ?code=)</div>
          <div className="mt-1 flex items-center gap-2">
            <code className="font-mono text-xs break-all bg-bg-base px-2 py-1 rounded">{issued.redeem_url}</code>
          </div>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={async () => { if (await copyText(issued.redeem_url)) { setCopied("url"); setTimeout(() => setCopied(null), 1500); } }}
            >
              <Copy className="h-3.5 w-3.5" /> 複製
              {copied === "url" && <span className="text-success">已複製</span>}
            </button>
            <a
              href={issued.redeem_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-xs"
            >
              <ExternalLink className="h-3.5 w-3.5" /> 開啟預覽
            </a>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-2xs text-text-muted uppercase tracking-wider">QR Code</div>
          <p className="mt-1 text-2xs text-text-muted">請新成員用手機相機掃描，直接開啟兌換頁面。</p>
          <div className="mt-2 inline-flex p-3 rounded-lg bg-white">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={`QR Code：${issued.code} 兌換 URL`}
                width={192}
                height={192}
                className="block"
              />
            ) : (
              <div className="h-[192px] w-[192px] flex items-center justify-center text-2xs text-text-muted">
                產生中…
              </div>
            )}
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-2xs">
          <dt className="text-text-muted">角色</dt><dd>{roleLabel(issued.role)}</dd>
          <dt className="text-text-muted">到期</dt><dd className="font-mono">{fmt(issued.expires_at)}</dd>
        </dl>
      </div>
      {onAnother && (
      <div className="flex justify-end">
        <button type="button" onClick={onAnother} className="btn-secondary text-sm">再產生一個</button>
      </div>
      )}
    </div>
  );
}
